import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  isDefaultActiveCriterion,
  PLATFORM_PLACEMENTS,
  PREFLIGHT_CRITERIA,
  SCORE_BAND_NEEDS_REVIEW_MIN,
  SCORE_BAND_STRONG_MIN,
  type PreflightPlatformCode,
} from "@nexus/contracts";
import { cn } from "@nexus/ui";
import { TONE_BG, TONE_TEXT, type ResultTone } from "../../constants/result-state";
import { PlatformLabel } from "../PlatformLabel/PlatformLabel";

const SCORE_MAX = 100;

const PLATFORM_CODES = Object.keys(
  PLATFORM_PLACEMENTS,
) as PreflightPlatformCode[];

/** Band ranges in best-first reading order; copy is shared with the score rail legend. */
const LEGEND_BANDS: readonly {
  band: string;
  tone: ResultTone;
  rangeKey: string;
  range: { min?: number; max?: number };
}[] = [
  {
    band: "STRONG",
    tone: "success",
    rangeKey: "preflight.rail.zoneStrong",
    range: { min: SCORE_BAND_STRONG_MIN },
  },
  {
    band: "NEEDS_REVIEW",
    tone: "warning",
    rangeKey: "preflight.rail.zoneNeedsReview",
    range: { min: SCORE_BAND_NEEDS_REVIEW_MIN, max: SCORE_BAND_STRONG_MIN - 1 },
  },
  {
    band: "AT_RISK",
    tone: "error",
    rangeKey: "preflight.rail.zoneAtRisk",
    range: { max: SCORE_BAND_NEEDS_REVIEW_MIN - 1 },
  },
];

function formatWeightPercent(weight: number): string {
  return `${weight.toFixed(2)}%`;
}

/** Only weighted criteria are listed — a zero-weight criterion cannot move the score. */
function scoredCriteriaForPlatform(platform: PreflightPlatformCode) {
  return PREFLIGHT_CRITERIA.filter(
    (criterion) =>
      criterion.platform === platform && isDefaultActiveCriterion(criterion),
  ).sort((a, b) => a.displayOrder - b.displayOrder);
}

export interface ScoringLogicInlineProps {
  /** Platform the panel opens on; users can switch tabs once inside (KTD8). */
  platform: PreflightPlatformCode;
  /** Target of the disclosure button's `aria-controls`. */
  id?: string;
  className?: string;
}

/**
 * Read-only explanation of the scoring model, sized to sit inside the create dialog
 * as a disclosure rather than pulling the user into the ScoringLogicDrawer sheet
 * mid-form. Lists the platform's weighted criteria (KTD8) and the band ranges a
 * resulting score falls into.
 */
export function ScoringLogicInline({
  platform,
  id,
  className,
}: ScoringLogicInlineProps) {
  const { t } = useTranslation();
  const [activePlatform, setActivePlatform] =
    useState<PreflightPlatformCode>(platform);

  useEffect(() => setActivePlatform(platform), [platform]);

  const criteria = useMemo(
    () => scoredCriteriaForPlatform(activePlatform),
    [activePlatform],
  );

  return (
    <div
      id={id}
      className={cn(
        "border-border border-t pt-4",
        "animate-in fade-in-0 slide-in-from-top-1 duration-200 motion-reduce:animate-none",
        className,
      )}
    >
      <h3 className="text-foreground text-[13px] font-semibold">
        {t("preflight.scoringInline.title")}
      </h3>

      <div
        // Hiding the scrollbar chrome is required, not cosmetic: `overflow-x`
        // forces `overflow-y` to `auto`, and the tabs' `-mb-px` overflows by a
        // pixel, so the strip renders a vertical scrollbar with nothing to scroll.
        // `!` is needed because globals.css sets `* { scrollbar-width: thin }`
        // unlayered, which otherwise outranks any Tailwind utility.
        className="border-border mt-2 flex gap-1 overflow-x-auto border-b [scrollbar-width:none]! [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label={t("preflight.scoringDrawer.platformAriaLabel")}
      >
        {PLATFORM_CODES.map((code) => {
          const isSelected = activePlatform === code;
          return (
            <button
              key={code}
              type="button"
              role="tab"
              aria-selected={isSelected}
              onClick={() => setActivePlatform(code)}
              className={cn(
                "-mb-px shrink-0 border-b-2 px-3 py-1.5 text-xs transition-colors",
                isSelected
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <PlatformLabel platform={code} />
            </button>
          );
        })}
      </div>

      <div role="tabpanel">
        <p className="text-muted-foreground mt-3 max-w-[520px] text-xs">
          {t("preflight.scoringInline.description", {
            count: criteria.length,
            platform: t(`preflight.platform.${activePlatform}`),
            max: SCORE_MAX,
            defaultValue_one:
              "Each creative is checked against {{count}} {{platform}} best-practice criterion. Pass earns full weight, Partial half, Fail none — summed into a 0–{{max}} score.",
            defaultValue_other:
              "Each creative is checked against {{count}} {{platform}} best-practice criteria of equal weight. Pass earns full weight, Partial half, Fail none — summed into a 0–{{max}} score.",
          })}
        </p>

        <ol className="mt-3 flex flex-col">
          {criteria.map((criterion) => (
            <li
              key={criterion.code}
              className="border-border/50 flex items-start gap-3 border-t py-2"
            >
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-foreground text-[13px] font-medium">
                  {t(
                    `preflight.catalogue.${criterion.platform}.${criterion.code}.name`,
                    criterion.name,
                  )}
                </span>
                <span className="text-muted-foreground text-xs">
                  {t(
                    `preflight.catalogue.${criterion.platform}.${criterion.code}.definition`,
                    criterion.definition,
                  )}
                </span>
              </span>
              <span className="text-muted-foreground shrink-0 text-xs font-medium tabular-nums">
                {formatWeightPercent(criterion.defaultWeight)}
              </span>
            </li>
          ))}
        </ol>
      </div>

      <ul
        className="border-border mt-3 flex flex-wrap gap-x-4 gap-y-1.5 border-t pt-3 text-xs"
        aria-label={t("preflight.rail.legendTitle")}
      >
        {LEGEND_BANDS.map((zone) => (
          <li
            key={zone.band}
            className={cn("inline-flex items-center gap-1.5", TONE_TEXT[zone.tone])}
          >
            <span
              className={cn("size-2 shrink-0 rounded-full", TONE_BG[zone.tone])}
              aria-hidden
            />
            <span>
              {t("preflight.rail.zoneLegend", {
                band: t(`preflight.band.${zone.band}`),
                range: t(zone.rangeKey, zone.range),
              })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
